import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { Question, QuestionsData } from '../types/api';
import QuestionBasedForm from './QuestionBasedForm';
import './VisibilityChecker.css';

const VisibilityChecker: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<number>(0); // First card open by default

  // Handle prefilled data when returning from verification page
  const prefillData = location.state?.prefillData;

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        setError(null);
        const data: QuestionsData = await apiService.getQuestions();
        setQuestions(data.items || []);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch questions');
        console.error('Error fetching questions:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, []);

  const handleFormSubmit = async (formData: Record<string, string>) => {
    setIsSubmitting(true);
    try {
      // Extract email from form data
      const email = formData.email_address || formData.email;
      if (!email) {
        throw new Error('Email address is required');
      }

      // Save user responses immediately
      try {
        const businessInfo = {
          name: formData.company_name || formData.business_name || formData.name || '',
          domain: formData.website || formData.domain || formData.website_url || '',
          industry: formData.industry || formData.business_type || formData.service_type || '',
          city: formData.city || formData.location || '',
          state: formData.state || formData.province || '',
          country: formData.country || 'US'
        };

        const businessFields = ['company_name', 'business_name', 'name', 'website', 'domain', 'website_url', 'industry', 'business_type', 'service_type', 'city', 'location', 'state', 'province', 'country', 'email_address', 'email'];
        const responses: Record<string, any> = {};
        
        Object.entries(formData).forEach(([key, value]) => {
          if (!businessFields.includes(key)) {
            responses[key] = value;
          }
        });

        await apiService.saveUserResponses({
          business_info: businessInfo,
          responses: responses,
          user_email: email
        });

        console.log('✅ User responses saved successfully');
      } catch (saveError: any) {
        console.error('Error saving user responses:', saveError);
        // Don't block the flow if saving fails
        alert('Warning: Could not save your responses. Please try again.');
      }

      // Send verification code
      await apiService.sendVerificationCode({ email });
      
      // Navigate to verification page with form data
      navigate('/verify-email', { 
        state: { email, formData },
        replace: true 
      });
      
    } catch (err: any) {
      console.error('Error sending verification code:', err);
      alert('Error sending verification code: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? -1 : index);
  };

  if (loading) {
    return (
      <div className="visibility-checker">
        <div className="container">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading questions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="visibility-checker">
        <div className="container">
          <div className="error-container">
            <h2>Error Loading Content</h2>
            <p className="error-message">{error}</p>
            <button 
              className="btn btn-primary" 
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="visibility-checker">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content text-center">
            <h1 className="hero-title">
              See how your <span className="highlight">business ranks</span>
              <br />
              in <span className="highlight-red">AI Search Results</span>
            </h1>
            <p className="hero-subtitle">
              Discover your visibility across ChatGPT, Perplexity, Grok, and other AI platforms
              that your customers are already using to find services like yours.
            </p>
            <a href="#ranking-form" className="btn btn-primary hero-cta">
              Check your AI Ranking
            </a>
            
            <div className="powered-by">
              <p>POWERED BY</p>
              <div className="platform-logos">
                <span className="platform-logo"><img src="/OpenAI.svg" alt="OpenAI" /></span>
                <span className="platform-logo"><img src="/Gemini.svg" alt="Gemini" /></span>
                <span className="platform-logo"><img src="/Grok.svg" alt="Grok" /></span>
                <span className="platform-logo"><img src="/Perplexity.svg" alt="Perplexity" /></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why AI Search Matters Section */}
      <section className="why-section py-12">
        <div className="container">
          <h2 className="section-title text-center">
            Why AI Search Matters for Home Service Companies
          </h2>
          
          <div className="why-section-grid">
            <div className="content-text">
              <h3>The New Search Reality</h3>
              <p>
                The landscape of online search is rapidly evolving with AI-powered 
                search engines transforming how consumers find information.
              </p>
              <blockquote className="quote">
                "New research from Bain & Company finds 80% of consumers 
                now rely on AI search results for at least 40% of their searches, 
                reducing organic web traffic by 15% to 25%."
              </blockquote>
              <p>
                This shift in search behavior means businesses need to adapt their 
                strategies to remain visible in this new AI-first landscape.
              </p>
            </div>
            
            <div className="stats-container">
              <div className="stat-card">
                <div className="stat-number">80%</div>
                <div className="stat-label">of consumers rely on AI search results</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">15-25%</div>
                <div className="stat-label">reduction in organic web traffic</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ranking Form Section */}
      <section id="ranking-form" className="ranking-section py-12">
        <div className="container">
          <div className="form-container">
            <QuestionBasedForm 
              questions={questions}
              onSubmit={handleFormSubmit}
              isSubmitting={isSubmitting}
              initialData={prefillData}
            />
          </div>
        </div>
      </section>

      {/* More Information Section */}
      <section className="questions-section py-12">
        <div className="container">
          <h2 className="section-title text-center">More Information</h2>
          
          <div className="questions-container">
            <div className={`question-card ${openFAQ === 0 ? 'active' : ''}`} onClick={() => toggleFAQ(0)}>
              <h3 className="question-title">
                <span className="question-icon">Q</span>
                What exactly is an AI Search Engine?
                <span className="toggle-icon">{openFAQ === 0 ? '−' : '+'}</span>
              </h3>
              <div className="question-content">
                <p>Instead of showing a list of links like Google, AI search engines give direct answers based on context, relevance, and reputation. They can summarize reviews, recommend local pros, or highlight brands they "trust." Tools like ChatGPT, Perplexity, and Gemini are already influencing homeowner decisions — often before they even click a link.</p>
              </div>
            </div>
            
            <div className={`question-card ${openFAQ === 1 ? 'active' : ''}`} onClick={() => toggleFAQ(1)}>
              <h3 className="question-title">
                <span className="question-icon">Q</span>
                Why should contractors care about AI Search Engines?
                <span className="toggle-icon">{openFAQ === 1 ? '−' : '+'}</span>
              </h3>
              <div className="question-content">
                <p>Homeowners are no longer just turning to Google — they're asking AI tools like ChatGPT, Perplexity, and Gemini for help finding local pros. If your company isn't showing up in those results, you're already behind. Whether it's "best plumber near me" or "how much does it cost to fix a roof leak," AI search engines are rewriting how people find and choose contractors. Understanding how your brand shows up in these new tools helps you win more jobs, build trust, and stay ahead of competitors in your market.</p>
              </div>
            </div>
            
            <div className={`question-card ${openFAQ === 2 ? 'active' : ''}`} onClick={() => toggleFAQ(2)}>
              <h3 className="question-title">
                <span className="question-icon">Q</span>
                Which AI platforms does this tool scan?
                <span className="toggle-icon">{openFAQ === 2 ? '−' : '+'}</span>
              </h3>
              <div className="question-content">
                <p>Our tool checks your brand's visibility on: GPT-4o (ChatGPT), Grok (X), Perplexity, Gemini (Google). We're actively expanding to include more platforms as AI search evolves.</p>
              </div>
            </div>
            
            <div className={`question-card ${openFAQ === 3 ? 'active' : ''}`} onClick={() => toggleFAQ(3)}>
              <h3 className="question-title">
                <span className="question-icon">Q</span>
                How often can I use this tool?
                <span className="toggle-icon">{openFAQ === 3 ? '−' : '+'}</span>
              </h3>
              <div className="question-content">
                <p>You can check your AI visibility as often as you'd like. We recommend running checks monthly to track your progress and see how algorithm updates affect your rankings across different AI platforms.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VisibilityChecker;
